import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Loader2, Download, Upload } from 'lucide-react';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import { VersionProvider } from '@/contexts/VersionContext';
import VersionAwareWrapper from '@/components/company/VersionAwareWrapper';
import ErrorBoundary from '@/components/ErrorBoundary';
import CompanyNavigation from '@/components/company/CompanyNavigation';
import CompanyHeader, { CompanyHeaderSkeleton } from '@/components/company/CompanyHeader';
import PageTabs from '@/components/company/PageTabs';
import ContentTabs from '@/components/company/ContentTabs';
import EditCompanyModal from '@/components/company/EditCompanyModal';
import PageTags from '@/components/companies/PageTags';
import TaskStatusIndicator from '@/components/company/TaskStatusIndicator';
import { Button } from '@/components/ui/button';
import useCompanyDetailsLogic from './useCompanyDetailsLogic';
import PageScreenshotViewer from '@/components/pages/PageScreenshotViewer';
import PageEventViewer from '@/components/pages/PageEventViewer';
import CompanyVersions from '@/components/company/CompanyVersions';
import PageAskAI from '@/components/pages/PageAskAI';
import CompanyPlans from '@/components/company/CompanyPlans';
import CompanyPricingMetrics from '@/components/companies/CompanyPricingMetrics';
import FilterSummary from '@/components/FilterSummary';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAppSelector } from '@/hooks/useAppSelector';
import { graphqlClient } from '@/utils/graphqlClient';
import { gql } from 'graphql-request';
import { fetchExtractTimestamps, fetchLatestExtract, formatExtractDate, generateChangeSummary, hasObjectDifferences } from '@/utils/extractUtils';
import { detectQuarterChanges } from '@/utils/quarterChanges';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const TabLoadingFallback = ({ tabName }: { tabName: string }) => (
  <div className="flex flex-col items-center justify-center h-64 w-full">
    <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-2" />
    <p className="text-gray-500">Loading {tabName}...</p>
  </div>
);

const CompanyDetailsPage: React.FC = () => {
  const {
    slug,
    data: company,
    isLoading,
    error,
    refetch,
    activeTab,
    activePage,
    showEditDialog,
    setShowEditDialog,
    handlePageChange,
    handleTabChange,
    isTabContentVisible,
    navigateToNextCompany,
    navigateToPreviousCompany
  } = useCompanyDetailsLogic();

  const { toast } = useToast();
  const graphQLData = useAppSelector((state) => state.graphql.data);
  const [lastPublishedDate, setLastPublishedDate] = useState<string | null>(null);
  const [allTimestamps, setAllTimestamps] = useState<string[]>([]);
  const [isLoadingExtract, setIsLoadingExtract] = useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
  const [latestExtractData, setLatestExtractData] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isComparingData, setIsComparingData] = useState(false);
  const [changeSummary, setChangeSummary] = useState<{
    added: string[];
    modified: string[];
    removed: string[];
    addedCount: number;
    modifiedCount: number;
    removedCount: number;
    totalChanges: number;
  }>({ added: [], modified: [], removed: [], addedCount: 0, modifiedCount: 0, removedCount: 0, totalChanges: 0 });

  // Fetch the latest extract timestamp and content
  const fetchLastExtractTimestamp = async () => {
    if (!slug) {
      console.log('No slug available for fetching extract timestamp');
      return;
    }
    
    const cleanSubslug = activePage ? activePage.replace(/^\./, '') : '';
    const fullSlug = cleanSubslug && cleanSubslug !== 'main' ? `${slug}.${cleanSubslug}` : slug;
    console.log(`Fetching extracts for slug: ${fullSlug}`);
    
    setIsLoadingExtract(true);
    try {
      // Get timestamps for the specific slug
      const timestamps = await fetchExtractTimestamps(fullSlug);
      console.log('Extract timestamps:', timestamps);
      
      if (timestamps && timestamps.length > 0) {
        console.log(`Found ${timestamps.length} extract timestamps:`, timestamps);
        
        // Get the most recent timestamp (already sorted in utility function)
        const mostRecentTimestamp = timestamps[0];
        console.log('Most recent timestamp:', mostRecentTimestamp);
        
        // Format the date for display
        const formattedDate = formatExtractDate(mostRecentTimestamp);
        setLastPublishedDate(formattedDate);
        
        // Store all timestamps for display in the modal
        setAllTimestamps(timestamps);
        
        // Also fetch the latest extract content for comparison
        setIsComparingData(true);
        const extractData = await fetchLatestExtract(fullSlug);
        setLatestExtractData(extractData);
        
        // Compare with current data
        if (extractData && graphQLData) {
          console.log('[CompanyDetailsPage] GraphQL data or extract data changed, re-comparing...');
          
          console.log('[CompanyDetailsPage] Comparing current GraphQL data with latest extract');
          
          // Debug the structure of charges and their versions before comparing
          if (graphQLData.pages && graphQLData.pages[0] && graphQLData.pages[0].plans) {
            const examplePlan = graphQLData.pages[0].plans[0];
            if (examplePlan && examplePlan.charges && examplePlan.charges.length > 0) {
              const exampleCharge = examplePlan.charges[0];
              console.log('[DEBUG] Example charge structure:', {
                id: exampleCharge.id,
                type: exampleCharge.type,
                hasVersionsProp: !!exampleCharge.versions,
                versions: exampleCharge.versions,
              });
            }
          }
          
          const summary = generateChangeSummary(graphQLData, extractData);
          console.log('[DEBUG] Comparison result:', {
            totalChanges: summary.totalChanges,
            chargeChanges: summary.modified.length
          });
          
          setChangeSummary(summary);
          
          // Set the change status based on the summary's total changes
          // This ensures the indicator and summary are ALWAYS in sync
          const currentDataHasChanges = summary.totalChanges > 0;
          setHasChanges(currentDataHasChanges);
          
          console.log(`Extract comparison result: ${currentDataHasChanges ? 'Changes detected' : 'No changes'}`);
          if (currentDataHasChanges) {
            console.log('Change summary:', summary);
          }
        } else {
          setHasChanges(true); // Assume changes if no extract data
          setChangeSummary({ added: [], modified: [], removed: [], addedCount: 0, modifiedCount: 0, removedCount: 0, totalChanges: 0 });
        }
        
        setIsComparingData(false);
      } else {
        console.log('No extract timestamps found');
        setLastPublishedDate(null);
        setLatestExtractData(null);
        setHasChanges(true); // Assume changes if no previous extract exists
      }
    } catch (error) {
      console.error('Failed to fetch extract timestamp:', error);
    } finally {
      console.log('Extract timestamp fetch completed, loading state set to false');
      setIsLoadingExtract(false);
    }
  };

  useEffect(() => {
    if (slug) {
      fetchLastExtractTimestamp();
    }
  }, [slug, activePage]);
  
  // Keep track of the last time graphQLData was updated
  const lastGraphQLDataUpdateRef = useRef<number>(Date.now());
  
  // This effect runs on mount or when the slug changes to establish a baseline
  useEffect(() => {
    if (graphQLData) {
      lastGraphQLDataUpdateRef.current = Date.now();
      console.log('Initial GraphQL data reference saved for change detection');
    }
  }, [slug]);
  
  // Re-compare data whenever GraphQL data changes
  // This detects both initial loads and user edits
  useEffect(() => {
    if (!graphQLData || !latestExtractData) return;
    
    // Track when this update happened
    const now = Date.now();
    const timeSinceLastUpdate = now - lastGraphQLDataUpdateRef.current;
    
    // Update our reference time
    lastGraphQLDataUpdateRef.current = now;
    
    // Log whether this is likely an initial load or a user edit
    const isLikelyUserEdit = timeSinceLastUpdate > 1000; // If more than 1 second has passed since initial load
    console.log(`GraphQL data changed (${isLikelyUserEdit ? 'user edit detected' : 'initial load'})`);
    
    // Only re-compare if both datasets are available
    setIsComparingData(true);
    
    // Always generate the detailed change summary first
    const summary = generateChangeSummary(graphQLData, latestExtractData);
    setChangeSummary(summary);
    
    // Set the change status based on the summary's total changes
    // This ensures the indicator and summary are ALWAYS in sync
    const currentDataHasChanges = summary.totalChanges > 0;
    setHasChanges(currentDataHasChanges);
    
    console.log(`Re-comparing extract data with latest GraphQL data: ${currentDataHasChanges ? 'Changes detected' : 'No changes'}`);
    if (currentDataHasChanges) {
      console.log('Updated change summary:', summary);
    }
    
    setIsComparingData(false);
  }, [graphQLData, latestExtractData]);

  const handlePublishConfirmRequest = () => {
    // First check if we have GraphQL data to publish
    if (!graphQLData) {
      toast({
        variant: 'destructive',
        title: 'Cannot publish',
        description: 'No data available to publish. Please load the company data first.',
      });
      return;
    }

    // If we're still comparing data, show a loading toast
    if (isComparingData) {
      toast({
        title: 'Please wait',
        description: 'Still comparing data with the latest extract...',
      });
      return;
    }

    // Only allow publishing if there are changes detected
    if (!hasChanges) {
      toast({
        variant: 'default',
        title: 'No changes detected',
        description: 'The current data is identical to the latest published extract. No need to publish.',
      });
      return;
    }

    // If we have changes, allow the confirmation dialog to show
    setShowPublishConfirmation(true);
  };

  const handlePublishExtract = async () => {
    setShowPublishConfirmation(false);
    if (!graphQLData) {
      toast({
        variant: 'destructive',
        title: 'Publish failed',
        description: 'GraphQL data not loaded',
      });
      return;
    }

    const cleanSubslug = activePage ? activePage.replace(/^\./, '') : '';
    const fullSlug =
      cleanSubslug && cleanSubslug !== 'main' ? `${slug}.${cleanSubslug}` : slug;

    try {
      // Generate diffs for each page before publishing
      const enrichedGraphQLData = {
        ...graphQLData,
        pages: graphQLData?.pages?.map((page: any) => ({
          ...page,
          diffs: detectQuarterChanges(page)
        })) || []
      };
      
      const { data, error } = await supabase.functions.invoke('publish-extract', {
        body: { slug: fullSlug, extract: enrichedGraphQLData }
      });

      if (error || !data?.success) {
        toast({
          variant: 'destructive',
          title: 'Publish failed',
          description: error?.message || data?.error || 'Failed to publish extract',
        });
      } else {
        toast({
          title: 'Publish successful',
          description: `Extract published for ${fullSlug}`,
        });
        // Refresh the last published date
        fetchLastExtractTimestamp();
      }
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Publish failed',
        description: err.message || 'Error publishing extract',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <CompanyHeaderSkeleton />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-red-500">Failed to load company data.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" key={`company-details-${slug}`}>
      <CompanyNavigation
        onBack={() => window.history.back()}
        onPrevious={navigateToPreviousCompany}
        onNext={navigateToNextCompany}
        disableNavigation={false}
      />
      <FilterSummary compact={true} />
      <div className="mb-4">
        <div className="flex justify-between items-start">
          <div className="flex-grow">
            <CompanyHeader
              company={{ slug }}
              activePage={activePage}
              onEdit={() => setShowEditDialog(true)}
            />
          </div>
          <div className="ml-4 mt-4">
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  if (!graphQLData) return;
                  
                  // Create a Blob with the JSON data
                  const dataStr = JSON.stringify(graphQLData, null, 2);
                  const blob = new Blob([dataStr], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  
                  // Create a link element to download
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `${slug}_${activePage || 'main'}.json`;
                  
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  
                  // Clean up the URL object
                  URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-1"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export JSON</span>
              </Button>
              <div className="flex flex-col">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handlePublishConfirmRequest}
                        className="flex items-center"
                        disabled={!hasChanges || isComparingData}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">
                          {isComparingData ? 'Comparing...' : 'Publish Extract'}
                        </span>
                        {isComparingData ? (
                          <div className="w-2 h-2 rounded-full bg-slate-400 ml-2 animate-pulse" />
                        ) : !lastPublishedDate ? (
                          <div className="w-2 h-2 rounded-full bg-slate-400 ml-2" />
                        ) : changeSummary.totalChanges > 0 ? (
                          // Use changeSummary directly instead of hasChanges to ensure consistency
                          <div className="w-2 h-2 rounded-full bg-amber-500 ml-2 animate-pulse" />
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-green-500 ml-2" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="p-2">
                      {isLoadingExtract || isComparingData ? (
                        <span>Checking for changes...</span>
                      ) : lastPublishedDate ? (
                        <span>
                          {changeSummary.totalChanges > 0 ? "Changes available to publish" : "No changes to publish"}
                          <br />
                          <span className="text-xs">
                            Last published: <span className="font-medium">{lastPublishedDate}</span>
                          </span>
                        </span>
                      ) : (
                        <span>Never published</span>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {(isLoadingExtract || isComparingData) && (
                  <div className="text-xs text-muted-foreground mt-1">
                    <span>Checking for changes...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <PageTabs activePage={activePage} onPageChange={handlePageChange} slug={slug} />

      <ErrorBoundary>
        <VersionProvider>
          <div className="flex justify-between items-center mb-4">
            <ContentTabs activeTab={activeTab} onTabChange={handleTabChange} slug={slug} />
          </div>
        </VersionProvider>
      </ErrorBoundary>

      <div className={`mt-6 transition-opacity duration-300 ${isTabContentVisible ? 'opacity-100' : 'opacity-0'}`}>
        <Suspense fallback={<TabLoadingFallback tabName={activeTab} />}>
          <ErrorBoundary>
            <VersionProvider>
              {activeTab === 'plans' && (
                <VersionAwareWrapper componentName="CompanyPlans">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Company Plans</h2>
                    <TaskStatusIndicator slug={slug} />
                  </div>
                  <CompanyPlans slug={slug} subslug={activePage !== 'main' ? activePage : null} />
                  <div className='mt-9'>&nbsp;</div>
                  <CompanyPricingMetrics pageSubslug={activePage} refreshCompanyData={() => { void refetch(); }} />
                  <PageTags pageSubslug={activePage} />
                  <div className='mt-9'>&nbsp;</div>
                  <PageScreenshotViewer slug={slug} subslug={activePage !== 'main' ? activePage : null} />
                </VersionAwareWrapper>
              )}

              {activeTab === 'events' && (
                <VersionAwareWrapper componentName="PageEventViewer">
                  <PageEventViewer slug={slug} subslug={activePage !== 'main' ? activePage : null} />
                </VersionAwareWrapper>
              )}

              {activeTab === 'versions' && (
                <VersionAwareWrapper componentName="CompanyVersions">
                  <CompanyVersions slug={slug} subslug={activePage !== 'main' ? activePage : null} />
                </VersionAwareWrapper>
              )}

              {activeTab === 'ai' && (
                <VersionAwareWrapper componentName="PageAskAI">
                  <PageAskAI slug={slug} subslug={activePage !== 'main' ? activePage : null} />
                </VersionAwareWrapper>
              )}
            </VersionProvider>
          </ErrorBoundary>
        </Suspense>
      </div>

      {showEditDialog && company && (
        <EditCompanyModal
          company={{
            ...company,
            // Ensure all required properties from Redux Company type are present
            logoUrl: company.logo_url || null,
            employees: company.employees?.toString() || '',
            category: company.category || '',
            description: company.description || null,
            url: company.url || null,
            legacySlug: null,
            pricingMetrics: company.pages?.flatMap(p => p.pricing_metrics || []).map(metric => ({
              id: metric?.id || '',
              slug: metric?.display_name?.toLowerCase().replace(/ /g, '-') || '',
              display_name: metric?.display_name || '',
              modality: metric?.modality || null,
              subcategory: null,
              sample_values: metric?.sample_values || null,
              sample_prices: null,
              created_at: null
            })) || [],
            pages: company.pages?.map(page => ({
              id: page.id,
              subslug: page.subslug,
              pricingPageUrl: page.pricing_page_url || null,
              screenshotUrl: page.scrapes?.[0]?.screenshot_url || null,
              screenshotUrlVersion: page.scrapes?.[0]?.version || null,
              pricingMetrics: page.pricing_metrics || [],
              tags: page.tags || [],
              scrapes: page.scrapes || []
            })) || []
          }}
          isOpen={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          onCompanyUpdated={() => setShowEditDialog(false)}
        />
      )}
      
      <ConfirmationDialog
        isOpen={showPublishConfirmation}
        onOpenChange={setShowPublishConfirmation}
        onConfirm={handlePublishExtract}
        title="Publish Extract"
        description={
          <div className="space-y-3">
            <p>
              Are you sure you want to publish the extract for
              <span className="font-semibold"> {slug}{activePage && activePage !== 'main' ? `.${activePage}` : ''}</span>?
            </p>
            
            {allTimestamps.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium mb-1">Previous versions:</p>
                <div className="max-h-[120px] overflow-y-auto rounded-md border border-border bg-muted/30 p-2">
                  <ul className="text-xs space-y-1">
                    {allTimestamps.map((timestamp, index) => (
                      <li key={`timestamp-${index}`} className="flex items-center">
                        <span className="text-muted-foreground">{formatExtractDate(timestamp)}</span>
                        {index === 0 && <span className="ml-2 text-xs bg-green-100 text-green-800 rounded-full px-2 py-0.5">Latest</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            
            {/* Always display the change summary if we have changes */}
            <div className="mt-4 space-y-2 text-sm">
              <p className="font-medium">Changes summary:</p>
              <div className="rounded-md border border-border bg-muted/50 p-3 space-y-3 max-h-[300px] overflow-y-auto">
                {changeSummary.addedCount > 0 ? (
                  <div>
                    <p className="font-medium text-green-600">
                      Added ({changeSummary.addedCount}):
                    </p>
                    <ul className="ml-5 list-disc space-y-1">
                      {changeSummary.added.map((item, index) => (
                        <li key={`added-${index}`} className="text-green-700">{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                
                {changeSummary.modifiedCount > 0 ? (
                  <div>
                    <p className="font-medium text-amber-600">
                      Modified ({changeSummary.modifiedCount}):
                    </p>
                    <ul className="ml-5 list-disc space-y-1">
                      {changeSummary.modified.map((item, index) => (
                        <li key={`modified-${index}`} className="text-amber-700">{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                
                {changeSummary.removedCount > 0 ? (
                  <div>
                    <p className="font-medium text-red-600">
                      Removed ({changeSummary.removedCount}):
                    </p>
                    <ul className="ml-5 list-disc space-y-1">
                      {changeSummary.removed.map((item, index) => (
                        <li key={`removed-${index}`} className="text-red-700">{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                
                {/* When no changes are found */}
                {changeSummary.totalChanges === 0 && (
                  <p className="text-sm py-2">No changes detected. Publishing will create a new version with identical data.</p>
                )}
              </div>
            </div>
          </div>
        }
        confirmLabel="Publish"
        variant="success"
      />
    </div>
  );
};

export default CompanyDetailsPage;
